import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Sheet from 'components/utils/Sheet'
import { rem } from 'polished'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import useSocialProofPopup from 'hooks/OfferPage/useSocialProofPopup'

const DESKTOP_POPUP_WIDTH = 359
const MOBILE_IMAGE_SIZE = 56
const DESKTOP_IMAGE_SIZE = 48

const DialogPositioner = styled.div`
  position: fixed;
  inset-inline: ${rem(8)};
  bottom: calc(${rem(8)} + ${props => props.theme.layout.footerFloatingOffset});
  z-index: ${zIndex.notification};
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    top: calc(${rem(16)} + ${props => props.theme.layout.headerFloatingOffset} + ${props => props.theme.layout.headerRelativeOffset});
    inset-inline-end: ${rem(16)};
    bottom: auto;
    inset-inline-start: auto;
  }
`

const DialogCard = styled(Sheet)`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  width: 100%;
  pointer-events: auto;
  cursor: pointer;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)};
    width: ${rem(DESKTOP_POPUP_WIDTH)};
  }
`

const OfferImageContainer = styled.div`
  flex: 0 0 ${rem(MOBILE_IMAGE_SIZE)};
  width: ${rem(MOBILE_IMAGE_SIZE)};
  height: ${rem(MOBILE_IMAGE_SIZE)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};

  ${mediaQueryUp.tablet} {
    flex-basis: ${rem(DESKTOP_IMAGE_SIZE)};
    width: ${rem(DESKTOP_IMAGE_SIZE)};
    height: ${rem(DESKTOP_IMAGE_SIZE)};
  }
`

const Message = styled(BodyText)`
  flex: 1;
`

interface Props {
  offer: App.Offer
}

function SocialProofDialog({ offer }: Props) {
  const { dialog, dismissDialog } = useSocialProofPopup(offer.stats?.recentPurchases, offer.image)

  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    dismissDialog()
  }, [dismissDialog])

  if (!dialog) return null

  return <DialogPositioner>
    <DialogCard
      role="dialog"
      aria-label={dialog.message}
      aria-live="polite"
      aria-atomic="true"
      aria-modal="false"
      background="highlight-primary"
      shadow="bottom-large"
      borderRadius="L"
      padding={16}
      onClick={handleClick}
    >
      <OfferImageContainer>
        <Image
          image={dialog.offerImage}
          width={MOBILE_IMAGE_SIZE}
          height={MOBILE_IMAGE_SIZE}
          aspectRatio="1"
          fit="center"
          objectFit="cover"
        />
      </OfferImageContainer>
      <Message
        variant="large"
        colour="highlight-primary-light-contrast"
        wrap="word-wrap"
      >
        {dialog.message}
      </Message>
    </DialogCard>
  </DialogPositioner>
}

export default SocialProofDialog
